// hooks/billing/useAccountTier.js
import { useCallback, useEffect, useState } from 'react';
import BillingService from '../../services/billing.service';

/**
 * useAccountTier — `GET /accounts/{id}/tier`.
 *
 * Returns the full TierItem of the account (with all `max*` and the
 * `displayPrice` override). For Enterprise-pool accounts the tier may
 * be absent and the backend responds 404 TIER_NOT_FOUND — the caller
 * should branch on `error.code === 'TIER_NOT_FOUND'` and render an
 * appropriate state.
 *
 * @param {string} accountId
 * @param {Object} [options]
 * @param {boolean} [options.auto=true]
 */
export function useAccountTier(accountId, { auto = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(auto && accountId));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    const result = await BillingService.getAccountTier(accountId);
    if (result.success) {
      setData(result.data?.tier ?? null);
    } else {
      setError(result);
    }
    setLoading(false);
  }, [accountId]);

  useEffect(() => {
    if (auto && accountId) {
      refetch();
    } else if (!accountId) {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, [auto, accountId, refetch]);

  return { data, loading, error, refetch };
}

export default useAccountTier;
